/**
 * Shared paneled-door proportions for stile-and-rail assembly + hardware snap.
 * Handle mounts on the lock stile (X) and solid lock rail (Z).
 */

export type PaneledPanel = {
  id: string;
  /** Panel center in leaf-local Z (height axis), relative to leaf center. */
  z: number;
  w: number;
  h: number;
};

export type PaneledLeafLayout = {
  stile: number;
  /** Top and bottom rail height. */
  rail: number;
  /** Thin rail between top and mid panels. */
  muntin: number;
  /** Leaf-local Z of the muntin center. */
  muntinZ: number;
  /** Solid horizontal band for lever + keyhole. */
  lockRail: number;
  /** Leaf-local Z of the lock-rail center. */
  lockRailZ: number;
  /** Rail / panel width between the two stiles. */
  railW: number;
  panels: PaneledPanel[];
  /** Recessed panel thickness (Y) — clearly thinner than the frame. */
  fillThickness: number;
};

/**
 * Classic Swedish three-panel leaf: top / short mid / bottom, with a solid
 * lock rail between mid and bottom so hardware sits on frame, not filling.
 */
export function buildPaneledLayout(
  width: number,
  thickness: number,
  height: number,
): PaneledLeafLayout {
  // Wide enough that lever/knob/bar roses sit fully on the lock stile.
  const stile = Math.max(width * 0.2, 0.08);
  const rail = Math.max(height * 0.055, 0.065);
  const muntin = Math.max(height * 0.035, 0.04);
  // Tall enough for lever rose + keyhole (~0.19 apart) with margin.
  const lockRail = Math.max(height * 0.12, 0.24);
  const railW = Math.max(width - stile * 2, 0.04);
  const innerH = Math.max(height - rail * 2, 0.08);
  const midH = Math.max(Math.min(innerH * 0.12, 0.16), 0.08);
  const topH = Math.max(innerH * 0.36, 0.18);
  const botH = Math.max(innerH - topH - midH - muntin - lockRail, 0.14);
  const topZ = height / 2 - rail - topH / 2;
  const muntinZ = topZ - topH / 2 - muntin / 2;
  const midZ = muntinZ - muntin / 2 - midH / 2;
  const lockRailZ = midZ - midH / 2 - lockRail / 2;
  const botZ = lockRailZ - lockRail / 2 - botH / 2;
  return {
    stile,
    rail,
    muntin,
    muntinZ,
    lockRail,
    lockRailZ,
    railW,
    // Clearly recessed vs full-thickness stiles/rails — no overlap into the frame.
    fillThickness: Math.max(thickness * 0.45, 0.012),
    panels: [
      { id: "top", z: topZ, w: railW, h: topH },
      { id: "mid", z: midZ, w: railW, h: midH },
      { id: "bot", z: botZ, w: railW, h: botH },
    ],
  };
}

/**
 * Hinge-local X for hardware centered on the handle-side stile.
 */
export function paneledHandleX(
  leafWidth: number,
  stile: number,
  closedSign: 1 | -1,
): number {
  return closedSign * (leafWidth - stile / 2);
}

/**
 * Hinge-local Z for the lever rose: slightly above lock-rail center so the
 * keyhole (placed ~0.19 below in DoorHandle3D) still lands on the rail.
 */
export function paneledHandleZ(
  panelCenterZ: number,
  layout: Pick<PaneledLeafLayout, "lockRail" | "lockRailZ">,
): number {
  const roseOffset = Math.min(layout.lockRail * 0.28, 0.08);
  return panelCenterZ + layout.lockRailZ + roseOffset;
}

type DoorStyleForHandle = "flush" | "paneled" | "glazed";

/**
 * Snap hinge-local handle centers onto stile / lock rail for paneled & glazed leaves.
 * Flush keeps the geometry-provided placement.
 */
export function placeDoorHandleCenter(
  doorStyle: DoorStyleForHandle,
  leaf: {
    width: number;
    thickness: number;
    height: number;
    closedSign: 1 | -1;
    panelCenter: { x: number; y: number; z: number };
  },
  handleCenter: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  if (doorStyle === "paneled") {
    const layout = buildPaneledLayout(leaf.width, leaf.thickness, leaf.height);
    return {
      x: paneledHandleX(leaf.width, layout.stile, leaf.closedSign),
      y: handleCenter.y,
      z: paneledHandleZ(leaf.panelCenter.z, layout),
    };
  }
  if (doorStyle === "glazed") {
    const stile = Math.max(Math.min(leaf.width, leaf.height) * 0.2, 0.07);
    return {
      x: paneledHandleX(leaf.width, stile, leaf.closedSign),
      y: handleCenter.y,
      z: handleCenter.z,
    };
  }
  return handleCenter;
}
